import { useCreate, useRedirect } from "ra-core";
import { useSearchParams, Link } from "react-router";
import { useState, useMemo } from "react";
import {
  ArrowLeft,
  FileText,
  Briefcase,
  Calendar,
  DollarSign,
  Plus,
  Trash2,
  Save,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  LineItem,
  Job,
} from "../../providers/fakerest/dataGenerator/types";
import { JobSearchSelect } from "../jobs/JobSearchSelect";
import { JobCreateDialog } from "../jobs/JobCreateDialog";

const statusChoices = [
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Sent" },
  { value: "accepted", label: "Accepted" },
  { value: "declined", label: "Declined" },
];

export const ProposalCreate = () => {
  const [searchParams] = useSearchParams();
  const defaultJobId = searchParams.get("job_id");

  const [create, { isPending: isCreating }] = useCreate();
  const redirect = useRedirect();

  const [jobId, setJobId] = useState<string>(defaultJobId || "");
  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [status, setStatus] = useState<string>("draft");
  const [validUntil, setValidUntil] = useState<string>(
    // Default to 30 days from now
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
  );
  const [lineItems, setLineItems] = useState<LineItem[]>([
    { description: "", quantity: 1, unit_price: 0 },
  ]);

  const total = useMemo(
    () =>
      lineItems.reduce((sum, item) => sum + item.quantity * item.unit_price, 0),
    [lineItems],
  );

  const handleJobCreated = (job: Job) => {
    setJobId(String(job.id));
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(amount);

  const handleLineItemChange = (
    index: number,
    field: keyof LineItem,
    value: string | number,
  ) => {
    const newItems = [...lineItems];
    if (field === "description") {
      newItems[index].description = value as string;
    } else if (field === "quantity") {
      newItems[index].quantity = Number(value) || 0;
    } else if (field === "unit_price") {
      newItems[index].unit_price = Number(value) || 0;
    }
    setLineItems(newItems);
  };

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      { description: "", quantity: 1, unit_price: 0 },
    ]);
  };

  const removeLineItem = (index: number) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = () => {
    if (!jobId) {
      alert("Please select a job");
      return;
    }

    create(
      "proposals",
      {
        data: {
          job_id: Number(jobId),
          status,
          valid_until: validUntil,
          line_items: lineItems.filter(
            (item) => item.description.trim() !== "",
          ),
          total,
          created_at: new Date().toISOString(),
        },
      },
      {
        onSuccess: (data) => {
          redirect("show", "proposals", data.id);
        },
      },
    );
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      {/* Back button */}
      <Link
        to="/proposals"
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Proposals
      </Link>

      {/* Header */}
      <h1 className="text-2xl font-semibold mb-6 flex items-center gap-2">
        <FileText className="h-6 w-6" />
        Create New Proposal
      </h1>

      {/* Form Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Proposal Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Job Selector */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Job <span className="required-asterisk">*</span>
            </Label>
            <JobSearchSelect
              value={jobId}
              onChange={setJobId}
              placeholder="Search for a job..."
              onCreateNew={() => setJobDialogOpen(true)}
            />
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusChoices.map((choice) => (
                  <SelectItem key={choice.value} value={choice.value}>
                    {choice.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Valid Until */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Valid Until
            </Label>
            <Input
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              className="w-48"
            />
          </div>
        </CardContent>
      </Card>

      {/* Line Items Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Line Items
            </CardTitle>
            <Button variant="outline" size="sm" onClick={addLineItem}>
              <Plus className="h-4 w-4 mr-1" />
              Add Item
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40%]">Description</TableHead>
                <TableHead className="w-[15%]">Qty</TableHead>
                <TableHead className="w-[20%]">Unit Price</TableHead>
                <TableHead className="w-[15%] text-right">Total</TableHead>
                <TableHead className="w-[10%]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lineItems.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Input
                      value={item.description}
                      onChange={(e) =>
                        handleLineItemChange(
                          index,
                          "description",
                          e.target.value,
                        )
                      }
                      placeholder="Description"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min="0"
                      value={item.quantity}
                      onChange={(e) =>
                        handleLineItemChange(index, "quantity", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unit_price}
                      onChange={(e) =>
                        handleLineItemChange(
                          index,
                          "unit_price",
                          e.target.value,
                        )
                      }
                    />
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(item.quantity * item.unit_price)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeLineItem(index)}
                      disabled={lineItems.length === 1}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Total */}
          <div className="flex justify-end mt-4 pt-4 border-t">
            <div className="text-right">
              <span className="text-sm text-muted-foreground mr-4">Total:</span>
              <span className="text-2xl font-bold">
                {formatCurrency(total)}
              </span>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end mt-6">
            <Button onClick={handleSubmit} disabled={isCreating || !jobId}>
              <Save className="h-4 w-4 mr-2" />
              {isCreating ? "Creating..." : "Create Proposal"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Job Create Dialog */}
      <JobCreateDialog
        open={jobDialogOpen}
        onOpenChange={setJobDialogOpen}
        onSuccess={handleJobCreated}
      />
    </div>
  );
};
